import {
    action,
    IReactionDisposer,
    makeObservable,
    observable,
    reaction,
} from "mobx";
import {makeApiServerRequest} from "./serverConnection/makeApiServerRequest";
import {WsServerConnection} from "./serverConnection/wsServerConnection";
import {UserId} from "../utils/types";
import {PM_ACTIONS} from "./serverConnection/types";
import {PAGES} from "./ViewStore";
import {RootStoreModel} from "@stores/RootStore";

export type ActivityInfo = Map<string, Set<UserId>>;

const PING_INTERVAL = 55000;
const INACTIVITY_THRESHOLD = 50000;

export enum PageType {
    DOC = 'DOC',
    QUIZZ = 'QUIZZ',
    ACKNOWLEDGMENT = 'ACK',
}

export const getDocPath = (wsId: string, docId: string) => `${wsId}#${PageType.DOC}#${docId}`;

export class ActivityProvider {
    ownerId: string;
    wsServerConnection: WsServerConnection;

    activity: ActivityInfo = observable.map();
    activityByUser = new Map<UserId, string>();

    private rootStore: RootStoreModel;
    private lastActivityTime = Date.now();
    private isTabActive = document.visibilityState === 'visible';

    private activityCheckIntervalId?: number;
    private latestPageType?: PageType;
    private latestPageId?: string;

    private pingAutorunDisposer: IReactionDisposer;

    constructor(rootStore: RootStoreModel, ownerId: string, wsServerConnection: WsServerConnection) {
        this.rootStore = rootStore;
        this.ownerId = ownerId;
        this.wsServerConnection = wsServerConnection;

        wsServerConnection.subscribe('open', this.onWsOpen);
        wsServerConnection.subscribe('close', this.onWsClose);
        wsServerConnection.onMsg(this.onActivitySync);

        makeObservable(this, {
            activity: observable,
            activityByUser: observable,
            setActivity: action,
            partialActivityUpdate: action,
        });

        this.subscribeToActivity();
        ['mousemove', 'keydown', 'click', 'scroll'].forEach(event =>
            document.addEventListener(event, this.updateActivity)
        );

        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.pingAutorunDisposer = reaction(
            () => ({
                currentPath: this.rootStore.view.page,
                pathParams: this.rootStore.view.pathParams
            }),
            this.onPathChange
        );
    }

    /** ------------------- Ping Logic ------------------- **/

    private onPathChange = ({currentPath, pathParams}: { currentPath: string; pathParams: any }) => {
        const {pageType, pageId} = this.getPingTarget(currentPath, pathParams);

        this.latestPageType = pageType;
        this.latestPageId = pageId;

        this.restartPingInterval();
    }

    private getPingTarget(currentPath: string, pathParams: any): { pageType?: PageType, pageId?: string } {
        switch (currentPath) {
            case PAGES.DOC_EDIT:
            case PAGES.DOC_HISTORY:
                return {pageType: PageType.DOC, pageId: pathParams.id};
            case PAGES.QUIZZES:
                return {pageType: PageType.QUIZZ};
            case PAGES.QUIZ_VIEW:
            case PAGES.QUIZ_TAKE:
            case PAGES.QUIZ_EDIT:
                return {pageType: PageType.QUIZZ, pageId: pathParams.id};
            case PAGES.ACKNOWLEDGMENTS_OVERVIEW:
                return {pageType: PageType.ACKNOWLEDGMENT};
            default:
                return {};
        }
    }

    private restartPingInterval() {
        if (this.activityCheckIntervalId) clearInterval(this.activityCheckIntervalId);

        const makePing = () => {
            const isActive = this.isTabActive && (Date.now() - this.lastActivityTime < INACTIVITY_THRESHOLD);
            if (isActive) {
                makeApiServerRequest(
                    this.wsServerConnection,
                    `a/p`,
                    {
                        wsId: this.rootStore.view.workspace?.id,
                        pageType: this.latestPageType,
                        id: this.latestPageId,
                    },
                );
            }
        };

        makePing();
        this.activityCheckIntervalId = setInterval(makePing, PING_INTERVAL) as unknown as number;
    }

    /** ------------------- User Activity ------------------- **/
    updateActivity = () => {
        this.lastActivityTime = Date.now();
    };

    /** ------------------- Tab Visibility ------------------- **/

    onVisibilityChange = () => {
        this.isTabActive = document.visibilityState === 'visible';
        if (this.isTabActive) {
            this.restartPingInterval();
        } else {
            if (this.activityCheckIntervalId) clearInterval(this.activityCheckIntervalId);
        }
    }

    /** ------------------- WebSocket Events ------------------- **/

    private onWsOpen = () => {
        this.subscribeToActivity();
        this.restartPingInterval(); // in case we were paused
    }

    private onWsClose = () => {
        if (this.activityCheckIntervalId) clearInterval(this.activityCheckIntervalId);
    }

    /** ------------------- Activity Sync ------------------- **/

    private onActivitySync = (msg: any) => {
        if (msg.a === PM_ACTIONS.ACTIVITY_SYNC) {
            const incoming = msg.data;
            const newMap = new Map<string, Set<UserId>>();
            for (const path in incoming) {
                newMap.set(path, observable.set(incoming[path]));
            }
            this.setActivity(newMap);
        } else if (msg.a === PM_ACTIONS.ACTIVITY_PARTIAL) {
            this.partialActivityUpdate(msg.path, msg.userId, msg.action, msg.oldPath);
        }
    }

    setActivity(activity: ActivityInfo) {
        this.activity = observable.map(activity);
        this.activityByUser.clear();
        for (const [path, users] of this.activity.entries()) {
            for (const userId of users) {
                this.activityByUser.set(userId, path);
            }
        }
    }

    partialActivityUpdate(path: string, userId: string, action: 'leave' | 'enter', oldPath?: string) {
        let users = this.activity.get(path);
        if (!users) {
            users = observable.set();
            this.activity.set(path, users);
        }

        if (action === 'enter') {
            users.add(userId);
            if (oldPath && this.activity.has(oldPath)) {
                const oldUsers = this.activity.get(oldPath)!;
                oldUsers.delete(userId);
                if (!oldUsers.size) this.activity.delete(oldPath);
            }
            this.activityByUser.set(userId, path);
        } else {
            users.delete(userId);
            if (!users.size) this.activity.delete(path);
            if (this.activityByUser.get(userId) === path) {
                this.activityByUser.delete(userId);
            }
        }
    }

    isUserActive(targetUserId: string): boolean {
        return this.activityByUser.has(targetUserId);
    }

    /** ------------------- Server Subscriptions ------------------- **/

    private subscribeToActivity() {
        makeApiServerRequest(
            this.wsServerConnection,
            `a/s`, undefined);
    }

    private unSubscribeFromActivity() {
        makeApiServerRequest(
            this.wsServerConnection,
            `a/uns`, undefined);
    }

    /** ------------------- Cleanup ------------------- **/

    destroy() {
        this.pingAutorunDisposer();
        if (this.activityCheckIntervalId) clearInterval(this.activityCheckIntervalId);

        this.wsServerConnection.offMsg(this.onActivitySync);
        this.wsServerConnection.unsubscribe('close', this.onWsClose);
        this.wsServerConnection.unsubscribe('open', this.onWsOpen);
        this.unSubscribeFromActivity();

        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        ['mousemove', 'keydown', 'click', 'scroll'].forEach(event =>
            document.removeEventListener(event, this.updateActivity)
        );
    }
}
